// services/ServiceCategoryService.js - ReactJS (web)
import api from '../api/api';

class ServiceCategoryService {
  // ==================== PUBLIC ROUTES ====================

  /**
   * Get all categories with filters
   * GET /api/categories
   */
  async getAllCategories(params = {}) {
    try {
      const response = await api.get('/categories', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get category with subcategories
   * GET /api/category/:id
   */
  async getCategoryWithSubcategories(id) {
    try {
      const response = await api.get(`/category/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get categories for dropdown/select
   * GET /api/categories/select
   */
  async getCategoriesForSelect() {
    try {
      const response = await api.get('/categories/select');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get subcategories for dropdown/select
   * GET /api/category/:id/subcategories/select
   */
  async getSubcategoriesForSelect(categoryId) {
    try {
      const response = await api.get(`/category/${categoryId}/subcategories/select`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get category tree (hierarchical)
   * GET /api/categories/tree
   */
  async getCategoryTree() {
    try {
      const response = await api.get('/categories/tree');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get popular categories (by provider count)
   * GET /api/categories/popular
   */
  async getPopularCategories(limit = 10) {
    try {
      const response = await api.get('/categories/popular', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get categories by provider count
   * GET /api/categories/by-provider-count
   */
  async getCategoriesByProviderCount(minCount = 0, options = {}) {
    try {
      const response = await api.get('/categories/by-provider-count', {
        params: { minCount, ...options }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search subcategories
   * GET /api/category/:id/subcategories/search
   */
  async searchSubcategories(categoryId, searchTerm) {
    try {
      const response = await api.get(`/category/${categoryId}/subcategories/search`, {
        params: { q: searchTerm }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Create new category with validation
   * POST /api/category
   */
  async createCategory(categoryData) {
    try {
      const response = await api.post('/category', categoryData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update category
   * PUT /api/category/:id
   */
  async updateCategory(id, updateData) {
    try {
      const response = await api.put(`/category/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete category
   * DELETE /api/category/:id
   */
  async deleteCategory(id) {
    try {
      const response = await api.delete(`/category/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle category status
   * PATCH /api/category/:id/toggle-status
   */
  async toggleCategoryStatus(id) {
    try {
      const response = await api.patch(`/category/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle featured status
   * PATCH /api/category/:id/toggle-featured
   */
  async toggleFeaturedStatus(id) {
    try {
      const response = await api.patch(`/category/${id}/toggle-featured`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add subcategory
   * POST /api/category/:id/subcategories
   */
  async addSubcategory(categoryId, subcategoryData) {
    try {
      const response = await api.post(`/category/${categoryId}/subcategories`, subcategoryData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update subcategory
   * PUT /api/category/:categoryId/subcategories/:subcategoryId
   */
  async updateSubcategory(categoryId, subcategoryId, updateData) {
    try {
      const response = await api.put(
        `/category/${categoryId}/subcategories/${subcategoryId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove subcategory
   * DELETE /api/category/:categoryId/subcategories/:subcategoryId
   */
  async removeSubcategory(categoryId, subcategoryId) {
    try {
      const response = await api.delete(`/category/${categoryId}/subcategories/${subcategoryId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reorder categories
   * POST /api/categories/reorder
   */
  async reorderCategories(categoryOrders) {
    try {
      const response = await api.post('/categories/reorder', { categoryOrders });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk update categories
   * PATCH /api/categories/bulk
   */
  async bulkUpdateCategories(ids, updateData) {
    try {
      const response = await api.patch('/categories/bulk', { ids, updateData });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk delete categories
   * DELETE /api/categories/bulk
   */
  async bulkDeleteCategories(ids) {
    try {
      const response = await api.delete('/categories/bulk', { data: { ids } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Validate category name uniqueness
   * POST /api/categories/validate-name
   */
  async validateCategoryName(name, excludeId = null) {
    try {
      const response = await api.post('/categories/validate-name', { name, excludeId });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SUPER ADMIN ROUTES ====================

  /**
   * Get category analytics
   * GET /api/categories/analytics
   */
  async getCategoryAnalytics() {
    try {
      const response = await api.get('/categories/analytics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get category statuses
   */
  getCategoryStatuses() {
    return ['active', 'inactive'];
  }

  /**
   * Get category colors
   */
  getCategoryColors() {
    return [
      '#D42E1E', '#E67E22', '#F1C40F', '#2ECC71', '#3498DB',
      '#9B59B6', '#1ABC9C', '#E74C3C', '#2980B9', '#8E44AD'
    ];
  }

  /**
   * Get icon options
   */
  getIconOptions() {
    return [
      'fa-wrench', 'fa-tools', 'fa-hammer', 'fa-bolt', 'fa-water',
      'fa-paint-roller', 'fa-scissors', 'fa-cut', 'fa-car', 'fa-home',
      'fa-building', 'fa-hard-hat', 'fa-paint-brush', 'fa-bath',
      'fa-wind', 'fa-solar-panel', 'fa-faucet', 'fa-plug',
      'fa-cog', 'fa-cogs', 'fa-screwdriver', 'fa-bolt', 'fa-fire-extinguisher'
    ];
  }

  /**
   * Format category for display
   */
  formatCategory(category) {
    if (!category) return null;

    return {
      ...category,
      id: category._id,
      statusLabel: category.isActive ? 'Active' : 'Inactive',
      statusColor: category.isActive ? '#2ECC71' : '#95A5A6',
      isActive: category.isActive !== false,
      isFeatured: category.isFeatured || false,
      subcategoryCount: category.subcategories?.length || 0,
      formattedProviderCount: category.providerCount || 0,
      createdAt: category.createdAt ? new Date(category.createdAt) : null,
      updatedAt: category.updatedAt ? new Date(category.updatedAt) : null,
      timeAgo: category.createdAt ? this.getTimeAgo(category.createdAt) : null
    };
  }

  /**
   * Format subcategory for display
   */
  formatSubcategory(subcategory) {
    if (!subcategory) return null;

    return {
      ...subcategory,
      id: subcategory._id,
      displayName: subcategory.name,
      hasIcon: !!subcategory.icon,
      hasDescription: !!subcategory.description
    };
  }

  /**
   * Format categories list
   */
  formatCategories(categories) {
    if (!categories || !Array.isArray(categories)) return [];
    return categories.map(cat => this.formatCategory(cat));
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date) {
    if (!date) return null;
    const now = new Date();
    const diff = now - new Date(date);
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  /**
   * Validate category data
   */
  validateCategoryData(data) {
    const errors = [];

    if (!data.name) {
      errors.push('Category name is required');
    } else if (data.name.length < 2) {
      errors.push('Category name must be at least 2 characters');
    } else if (!/^[a-z0-9-]+$/.test(data.name)) {
      errors.push('Category name can only contain lowercase letters, numbers, and hyphens');
    }

    if (!data.displayName) {
      errors.push('Display name is required');
    } else if (data.displayName.length < 2) {
      errors.push('Display name must be at least 2 characters');
    }

    if (data.description && data.description.length > 500) {
      errors.push('Description cannot exceed 500 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate subcategory data
   */
  validateSubcategoryData(data) {
    const errors = [];

    if (!data.name) {
      errors.push('Subcategory name is required');
    } else if (data.name.length < 2) {
      errors.push('Subcategory name must be at least 2 characters');
    }

    if (data.description && data.description.length > 300) {
      errors.push('Description cannot exceed 300 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Build query params for category search
   */
  buildQueryParams(filters = {}) {
    const params = {};

    if (filters.isActive !== undefined) {
      params.isActive = filters.isActive;
    }

    if (filters.isFeatured !== undefined) {
      params.isFeatured = filters.isFeatured;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.minProviderCount !== undefined) {
      params.minProviderCount = filters.minProviderCount;
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.limit) {
      params.limit = filters.limit;
    }

    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }

    if (filters.sortOrder) {
      params.sortOrder = filters.sortOrder;
    }

    return params;
  }

  /**
   * Get category display name with count
   */
  getCategoryDisplayName(category, showCount = true) {
    if (!category) return '';
    const name = category.displayName || category.name;
    if (showCount && category.providerCount > 0) {
      return `${name} (${category.providerCount})`;
    }
    return name;
  }

  /**
   * Get subcategory display name
   */
  getSubcategoryDisplayName(subcategory) {
    if (!subcategory) return '';
    return subcategory.name;
  }

  /**
   * Check if category has subcategories
   */
  hasSubcategories(category) {
    return category?.subcategories && category.subcategories.length > 0;
  }

  /**
   * Get category by name
   */
  getCategoryByName(categories, name) {
    if (!categories || !name) return null;
    return categories.find(c => c.name === name || c.displayName === name);
  }

  /**
   * Get subcategory by name
   */
  getSubcategoryByName(category, name) {
    if (!category || !category.subcategories || !name) return null;
    return category.subcategories.find(s => s.name === name);
  }

  /**
   * Get category tree as flat list with indentation
   */
  getCategoryTreeFlat(tree, prefix = '') {
    const result = [];
    if (!tree) return result;

    tree.forEach(category => {
      result.push({
        ...category,
        displayName: `${prefix}${category.displayName}`,
        level: prefix.length / 2
      });

      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach(sub => {
          result.push({
            ...sub,
            displayName: `${prefix}  └─ ${sub.name}`,
            level: (prefix.length / 2) + 1,
            isSubcategory: true,
            parentId: category.id
          });
        });
      }
    });

    return result;
  }

  /**
   * Get category icon HTML class (for web)
   */
  getCategoryIconClass(iconName) {
    if (!iconName) return 'fa-folder';
    return iconName;
  }

  /**
   * Get category color style
   */
  getCategoryColorStyle(color) {
    if (!color) return {};
    return {
      backgroundColor: color,
      color: '#fff'
    };
  }
}

export default new ServiceCategoryService();